import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { UpdateNotification } from "./components/UpdateNotification";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { SkipLink } from "./components/accessibility/SkipLink";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const AddCatch = lazy(() => import("./pages/AddCatch"));
const BulkAddCatch = lazy(() => import("./pages/BulkAddCatch"));
const Profile = lazy(() => import("./pages/Profile"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Statistics = lazy(() => import("./pages/Statistics"));
const Analytics = lazy(() => import("./pages/Analytics"));
const PublicCatch = lazy(() => import("./pages/PublicCatch"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Install = lazy(() => import("./pages/Install"));
const Trips = lazy(() => import("./pages/Trips"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MapPage = lazy(() => import("./pages/MapPage"));
const BitePredictor = lazy(() => import("./pages/BitePredictor"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PWAInstallPrompt />
        <UpdateNotification />
        <OfflineIndicator />
        <BrowserRouter>
          <SkipLink />
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/add-catch" element={<AddCatch />} />
                <Route path="/edit-catch/:id" element={<AddCatch />} />
                <Route path="/bulk-add" element={<BulkAddCatch />} />
                <Route path="/trips" element={<Trips />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/install" element={<Install />} />
                <Route path="/catch/:id" element={<PublicCatch />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/bite-predictor" element={<BitePredictor />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
